import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth0 } from '@auth0/auth0-react';
import Sidebar from '../../components/sidebar';
import {
  getRoomDefinitions,
  updateRoomDefinitionPrice,
  getRoomDefinitionsByDesignPackage,
} from '../../api/roomDefinition';

export default function Configuration() {
  const { t } = useTranslation();
  const { getAccessTokenSilently } = useAuth0();
  const [roomDefinitions, setRoomDefinitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPrices, setEditingPrices] = useState({});
  const [filter, setFilter] = useState('all');

  const getRoomDefinitionId = (roomDef) => {
    if (roomDef.id) return roomDef.id;
    if (roomDef._links?.self?.href) {
      const match = roomDef._links.self.href.match(/\/(\d+)$/);
      if (match) return match[1];
    }
    return `${roomDef.roomType}_${roomDef.designPackage}_${roomDef.price}`;
  };

  const fetchRoomDefinitions = useCallback(async () => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();

      let response;
      if (filter === 'all') {
        response = await getRoomDefinitions(token, 0, 100);
      } else {
        response = await getRoomDefinitionsByDesignPackage(token, filter);
      }

      if (response) {
        const definitions =
          filter === 'all'
            ? response._embedded?.roomDefinitions || []
            : response._embedded?.roomDefinitions || [];
        setRoomDefinitions(definitions);
      }
    } catch (error) {
      console.error('Failed to fetch room definitions:', error);
    } finally {
      setLoading(false);
    }
  }, [filter, getAccessTokenSilently]);

  useEffect(() => {
    fetchRoomDefinitions();
  }, [filter, fetchRoomDefinitions]);

  const handlePriceEdit = (id, field, value) => {
    setEditingPrices((prev) => {
      const newState = {
        ...prev,
        [id]: {
          ...prev[id],
          [field]: value,
        },
      };
      return newState;
    });
  };

  const handlePriceSave = async (roomDefinition, roomId) => {
    try {
      const token = await getAccessTokenSilently();
      const editData = editingPrices[roomId];

      if (!editData) return;

      const newPrice = parseFloat(editData.price ?? roomDefinition.price);
      const newOldPrice = editData.oldPrice
        ? parseFloat(editData.oldPrice)
        : roomDefinition.oldPrice;

      let apiId = roomDefinition.id;
      if (!apiId && roomDefinition._links?.self?.href) {
        const [, extractedId] = roomDefinition._links.self.href.match(/\/(\d+)$/) || [];
        if (extractedId) apiId = extractedId;
      }

      if (!apiId) {
        return;
      }

      await updateRoomDefinitionPrice(token, apiId, newPrice, newOldPrice);

      setRoomDefinitions((prev) =>
        prev.map((rd) =>
          getRoomDefinitionId(rd) === roomId
            ? { ...rd, price: newPrice, oldPrice: newOldPrice }
            : rd,
        ),
      );

      setEditingPrices((prev) => {
        const newState = { ...prev };
        delete newState[roomId];
        return newState;
      });
    } catch (error) {
      console.error('Failed to update price:', error);
    }
  };

  const handlePriceCancel = (id) => {
    setEditingPrices((prev) => {
      const newState = { ...prev };
      delete newState[id];
      return newState;
    });
  };

  const formatRoomType = (roomType) => {
    return roomType
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const formatDesignPackage = (designPackage) => {
    return designPackage.charAt(0) + designPackage.slice(1).toLowerCase();
  };

  if (loading) {
    return (
      <div className="page-with-sidebar">
        <Sidebar />
        <div className="layout-container">
          <div className="configuration">
            <h3>{t('pages.configuration.title')}</h3>
            <div className="configuration__loading">Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-with-sidebar">
      <Sidebar />
      <div className="layout-container">
        <div className="configuration">
          <h3>{t('pages.configuration.title')}</h3>
          <div className="configuration__subtitle">{t('pages.configuration.subtitle')}</div>

          <div className="configuration__content">
            <div className="configuration__section">
              <h4>{t('pages.configuration.roomDefinitions.title')}</h4>
              <div className="configuration__subtitle">
                {t('pages.configuration.roomDefinitions.subtitle')}
              </div>

              <div className="configuration__filters">
                <label htmlFor="package-filter">
                  {t('pages.configuration.roomDefinitions.filterByPackage')}:
                </label>
                <select
                  id="package-filter"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="configuration__filter-select"
                >
                  <option value="all">
                    {t('pages.configuration.roomDefinitions.filters.all')}
                  </option>
                  <option value="GOLD">
                    {t('pages.configuration.roomDefinitions.filters.gold')}
                  </option>
                  <option value="SILVER">
                    {t('pages.configuration.roomDefinitions.filters.silver')}
                  </option>
                  <option value="BRONZE">
                    {t('pages.configuration.roomDefinitions.filters.bronze')}
                  </option>
                </select>
              </div>

              <div className="configuration__room-definitions">
                {roomDefinitions.length === 0 ? (
                  <div className="configuration__no-data">
                    {t('pages.configuration.roomDefinitions.noData')}
                  </div>
                ) : (
                  <table className="configuration__table">
                    <thead>
                      <tr>
                        <th>{t('pages.configuration.roomDefinitions.table.roomType')}</th>
                        <th>{t('pages.configuration.roomDefinitions.table.designPackage')}</th>
                        <th>{t('pages.configuration.roomDefinitions.table.currentPrice')}</th>
                        <th>{t('pages.configuration.roomDefinitions.table.oldPrice')}</th>
                        <th>{t('pages.configuration.roomDefinitions.table.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {roomDefinitions.map((roomDef) => {
                        const roomId = getRoomDefinitionId(roomDef);
                        return (
                          <tr key={roomId}>
                            <td>{formatRoomType(roomDef.roomType)}</td>
                            <td>
                              <span
                                className={`configuration__package-badge configuration__package-badge--${roomDef.designPackage.toLowerCase()}`}
                              >
                                {formatDesignPackage(roomDef.designPackage)}
                              </span>
                            </td>
                            <td>
                              {editingPrices[roomId] ? (
                                <input
                                  key={`price-${roomId}`}
                                  type="number"
                                  value={
                                    editingPrices[roomId]?.price !== undefined
                                      ? editingPrices[roomId].price
                                      : roomDef.price
                                  }
                                  onChange={(e) => handlePriceEdit(roomId, 'price', e.target.value)}
                                  className="configuration__price-input"
                                />
                              ) : (
                                <span className="configuration__price">€{roomDef.price}</span>
                              )}
                            </td>
                            <td>
                              {editingPrices[roomId] ? (
                                <input
                                  key={`oldPrice-${roomId}`}
                                  type="number"
                                  value={
                                    editingPrices[roomId]?.oldPrice !== undefined
                                      ? editingPrices[roomId].oldPrice
                                      : roomDef.oldPrice ?? ''
                                  }
                                  onChange={(e) =>
                                    handlePriceEdit(roomId, 'oldPrice', e.target.value)
                                  }
                                  className="configuration__price-input"
                                  placeholder="Old price (optional)"
                                />
                              ) : (
                                <span className="configuration__old-price">
                                  {roomDef.oldPrice ? `€${roomDef.oldPrice}` : '-'}
                                </span>
                              )}
                            </td>
                            <td>
                              {editingPrices[roomId] ? (
                                <div className="configuration__action-buttons">
                                  <button
                                    type="button"
                                    onClick={() => handlePriceSave(roomDef, roomId)}
                                    className="configuration__save-btn"
                                  >
                                    {t('pages.configuration.roomDefinitions.actions.save')}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handlePriceCancel(roomId)}
                                    className="configuration__cancel-btn"
                                  >
                                    {t('pages.configuration.roomDefinitions.actions.cancel')}
                                  </button>
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() =>
                                    setEditingPrices((prev) => ({
                                      ...prev,
                                      [roomId]: {
                                        price: roomDef.price,
                                        oldPrice: roomDef.oldPrice ?? '',
                                      },
                                    }))
                                  }
                                  className="configuration__edit-btn"
                                >
                                  {t('pages.configuration.roomDefinitions.actions.editPrice')}
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
